import { useState, useEffect } from 'react';

// Breakpoint definitions (in pixels)
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1440
};

/**
 * Determines the current screen size category based on window width
 * @param {number} width - Current window width in pixels
 * @returns {string} Screen size category ('mobile' | 'tablet' | 'desktop' | 'large')
 */
const getScreenSize = (width) => {
  if (width < BREAKPOINTS.mobile) {
    return 'mobile';
  } else if (width < BREAKPOINTS.tablet) {
    return 'tablet';
  } else if (width < BREAKPOINTS.desktop) {
    return 'desktop';
  } else {
    return 'large';
  }
};

/**
 * Debounce utility to prevent excessive function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @returns {Function} Debounced function
 */
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Custom hook to detect and track screen size changes
 * Provides screen size category and boolean helpers for responsive design
 *
 * @returns {Object} Screen size information
 * @returns {string} screenSize - Current size category ('mobile' | 'tablet' | 'desktop' | 'large')
 * @returns {boolean} isMobile - True if screen width < 768px
 * @returns {boolean} isTablet - True if screen width >= 768px and < 1024px
 * @returns {boolean} isDesktop - True if screen width >= 1024px and < 1440px
 * @returns {boolean} isLarge - True if screen width >= 1440px
 *
 * @example
 * const { isMobile, isTablet, screenSize } = useScreenSize();
 *
 * if (isMobile) {
 *   // Render mobile layout
 * }
 */
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState(() =>
    getScreenSize(window.innerWidth)
  );

  useEffect(() => {
    // Debounced resize handler (150ms delay to prevent excessive re-renders)
    const handleResize = debounce(() => {
      const newSize = getScreenSize(window.innerWidth);
      setScreenSize(newSize);
    }, 150);

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Boolean helpers for convenient conditional rendering
  const isMobile = screenSize === 'mobile';
  const isTablet = screenSize === 'tablet';
  const isDesktop = screenSize === 'desktop';
  const isLarge = screenSize === 'large';

  return {
    screenSize,
    isMobile,
    isTablet,
    isDesktop,
    isLarge
  };
};

export default useScreenSize;
